// React core and hooks
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

// Third-party libraries
import { Helmet } from "react-helmet"
import { Button, Form, Input, Modal, notification } from "antd"
import { useDispatch, useSelector } from "react-redux"

// Project utilities and constants
import Strings from "utils/strings"
import { API, Endpoints } from "utils/api"
import {
	circleToFeatureCollection,
	UBER_GEO_ZONE_ID,
	UberDeliveryArea,
} from "utils/geojson"
import {
	setBreadcrumb,
	setLoader,
	setOrders,
	setPageSize,
	setOrdersTableWidth,
	setOrdersStatusFilter,
	delayedDispatch,
} from "store/actions"
import {
	useBreadcrumbFilters,
	ALL_CONTRACTS_VALUE,
} from "hooks/useBreadcrumbFilters"

// Project components
import { ContentWrapper, Icon } from "components"
import ImportModal from "./components/ImportModal"
import ContentTable, { OrderStatus } from "./components/ContentTable"
import { GeoZone } from "./components/OrderAttributeFilterDropdown"
import { STATUS_FILTER_PRESETS } from "./components/StatusFilterDropdown"
import { getOrders, OrderFilters } from "./utils"
import { OrderDetailPanel, MultiOrderPanel } from "./components"
import { ConfirmationMapModal } from "./components/modals"
import AddressValidationModal, {
	callAddressValidationAPI,
} from "./components/AddressValidationModal"

// Styles
import "./styles.scss"

const Orders: React.FC = () => {
	const dispatch = useDispatch()

	// Redux variables - memoize to prevent unnecessary re-renders
	const rawFilters = useSelector((state: any) => state.filters) || {}
	const orderFilters = useMemo(
		() => ({
			business: rawFilters.business,
			// "All contracts" is a Routes-only concept (the global filter is
			// shared across screens) — this screen has no single contract to
			// scope by, so treat it the same as no contract selected.
			contract:
				rawFilters.contract === ALL_CONTRACTS_VALUE
					? null
					: rawFilters.contract,
			date: rawFilters.date,
		}),
		[rawFilters.business, rawFilters.contract, rawFilters.date]
	)
	const orders = useSelector((state: any) => state.orders || [])
	const pageSize = useSelector((state: any) => state.pageSize)
	const activeTab = useSelector(
		(state: any) => state.orderTab || "address_confirmation"
	)
	const tableWidth = useSelector((state: any) => state.ordersTableWidth) || 70
	const storedStatusFilter = useSelector(
		(state: any) => state.ordersStatusFilter
	)

	// Use global breadcrumb filters hook
	const { breadcrumbActions, deleteModal } = useBreadcrumbFilters({
		autoSelectFirst: false,
		onDeleteModalSuccess: () =>
			getOrders(
				dispatch,
				selectedStatus,
				search,
				orderFilters?.business,
				orderFilters?.contract,
				orderFilters?.date,
				selectedLot,
				pagination,
				setPagination,
				setDefaultLots,
				attributeFilters
			),
	})

	// Use stored status filter from Redux, or default to preparation preset
	const selectedStatus: OrderStatus[] =
		storedStatusFilter || STATUS_FILTER_PRESETS.preparation

	// Wrapper to update Redux when status changes
	const setSelectedStatus = (status: OrderStatus[]) => {
		dispatch(setOrdersStatusFilter(status))
	}

	// State variables
	const [defaultBusinesses, setDefaultBusinesses] = useState<any[]>([])
	const [defaultContracts, setDefaultContracts] = useState<any[]>([])
	const [showImportModal, setShowImportModal] = useState<string>("")
	const [showAddressValidationModal, setShowAddressValidationModal] = useState<
		string | undefined
	>()
	const [selectedOrders, setSelectedOrders] = useState<string[]>([]) // Holds the selected orders for bulk actions
	const [pagination, setPagination] = useState({
		page: 0,
		pageSize: pageSize || 1000, // Default page size if not set
		total: orders.length || 0, // Total number of items
	})
	const [search, setSearch] = useState("")
	// All order attribute filters (bag / phone / geo zone) travel in one object.
	const [attributeFilters, setAttributeFilters] = useState<OrderFilters>({})
	// GeoJSON zones for the currently selected contract (for the geo filter).
	const [geoZones, setGeoZones] = useState<GeoZone[]>([])
	const [selectedLot, setSelectedLot] = useState<string | undefined | null>("")
	const [defaultLots, setDefaultLots] = useState<any[]>([])
	const [showApprovalModal, setShowApprovalModal] = useState(false)
	const [approvalData, setApprovalData] = useState<any>(null)
	const [showBulkLotUpdateModal, setShowBulkLotUpdateModal] = useState(false)
	const [showConfirmModal, setShowConfirmModalState] = useState(false)

	// Refs for resizer
	const containerRef = useRef<HTMLDivElement>(null)
	const isDraggingRef = useRef(false)

	// Setup breadcrumb with filters
	useEffect(() => {
		delayedDispatch(
			setBreadcrumb(() => ({
				locations: [
					{
						text: Strings.sidebar.routes,
						icon: "route",
					},
				],
				actions: breadcrumbActions,
			}))
		)
	}, [breadcrumbActions])

	// Reset selected lot if active tab changes
	useEffect(() => {
		setSelectedLot("")
	}, [activeTab])

	// Set the page size in states whenever it changes in Redux state
	useEffect(() => {
		dispatch(setPageSize(pagination.pageSize))
	}, [dispatch, pagination.pageSize])

	// Get initial data for businesses and contracts
	useEffect(() => {
		getInitialData(
			dispatch,
			orderFilters?.business || null,
			setDefaultBusinesses,
			setDefaultContracts
		)
	}, [])

	// Load the selected contract's geojson zones for the geo filter. Reset the
	// geo filter whenever the contract changes (zones are contract-scoped).
	useEffect(() => {
		const contractId = orderFilters?.contract
		setAttributeFilters((prev) =>
			prev.geoZoneId === undefined ? prev : { ...prev, geoZoneId: undefined }
		)
		if (!contractId) {
			setGeoZones([])
			return
		}
		API.get({
			url: Endpoints.uriGeojsonZones(`?contractId=${contractId}`),
		})
			.then((response: any) => {
				if (response.ok) setGeoZones(response.data.results || [])
				else setGeoZones([])
			})
			.catch(() => setGeoZones([]))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [orderFilters?.contract])

	// Get the orders based on filters
	// Note: selectedStatus is intentionally excluded from dependencies to avoid duplicate requests.
	// Status filter changes are handled via onStatusFilterChange callback when dropdown closes.
	useEffect(() => {
		getOrders(
			dispatch,
			selectedStatus,
			search,
			orderFilters?.business,
			orderFilters?.contract,
			orderFilters?.date,
			selectedLot,
			pagination,
			setPagination,
			setDefaultLots,
			attributeFilters
		).then(() => {
			// Reset selected orders when orders are fetched
			setSelectedOrders([])
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		dispatch,
		selectedStatus,
		search,
		attributeFilters,
		pagination.page,
		pagination.pageSize,
		orderFilters,
	])

	// Handle panel close
	const handlePanelClose = useCallback(() => {
		setSelectedOrders([])
	}, [])

	// Handle orders update (for ChangeStatusButton)
	const handleSetOrders = useCallback(
		(updater: any[] | ((prev: any[]) => any[])) => {
			if (typeof updater === "function") {
				dispatch(setOrders(updater(orders)))
			} else {
				dispatch(setOrders(updater))
			}
		},
		[dispatch, orders]
	)

	// Confirmation modal state + promise bridge for address_confirmation → picking
	const confirmResolveRef = useRef<((confirmed: boolean) => void) | null>(null)

	const handleModalConfirmation = useCallback((_nextStatus: string) => {
		return new Promise<boolean>((resolve) => {
			confirmResolveRef.current = resolve
			setShowConfirmModalState(true)
		})
	}, [])

	const handleSetShowConfirmModal = useCallback((show: boolean) => {
		if (!show) {
			confirmResolveRef.current?.(false)
			confirmResolveRef.current = null
		}
		setShowConfirmModalState(show)
	}, [])

	const handleConfirmModal = useCallback(() => {
		confirmResolveRef.current?.(true)
		confirmResolveRef.current = null
		setShowConfirmModalState(false)
	}, [])

	// Memoize selected orders Set for O(1) lookups
	const selectedOrdersSet = useMemo(
		() => new Set(selectedOrders),
		[selectedOrders]
	)

	// Memoize filtered selected orders for MultiOrderPanel
	const filteredSelectedOrders = useMemo(
		() => orders.filter((order: any) => selectedOrdersSet.has(order._id)),
		[orders, selectedOrdersSet]
	)

	// Handle change lot action from panels
	const handleChangeLot = useCallback((orderIds: string[]) => {
		setSelectedOrders(orderIds)
		setShowBulkLotUpdateModal(true)
	}, [])

	// Handle resize drag
	const handleMouseDown = useCallback((e: React.MouseEvent) => {
		e.preventDefault()
		isDraggingRef.current = true
		document.body.style.cursor = "col-resize"
		document.body.style.userSelect = "none"
	}, [])

	// Handle double-click to reset to default width
	const handleDoubleClick = useCallback(() => {
		dispatch(setOrdersTableWidth(70))
	}, [dispatch])

	// Mouse move and mouse up handlers for resizing
	useEffect(() => {
		const handleMouseMove = (e: MouseEvent) => {
			if (!isDraggingRef.current || !containerRef.current) return

			const containerRect = containerRef.current.getBoundingClientRect()
			const newWidth =
				((e.clientX - containerRect.left) / containerRect.width) * 100

			// Constrain width between 30% and 85%
			const clampedWidth = Math.min(Math.max(newWidth, 30), 85)
			dispatch(setOrdersTableWidth(clampedWidth))
		}

		const handleMouseUp = () => {
			if (isDraggingRef.current) {
				isDraggingRef.current = false
				document.body.style.cursor = ""
				document.body.style.userSelect = ""
			}
		}

		document.addEventListener("mousemove", handleMouseMove)
		document.addEventListener("mouseup", handleMouseUp)

		return () => {
			document.removeEventListener("mousemove", handleMouseMove)
			document.removeEventListener("mouseup", handleMouseUp)
		}
	}, [dispatch])

	// Delivery zones of the currently selected contract, shared by the order maps.
	const selectedContractId = orderFilters?.contract
	const selectedContract = useMemo(
		() =>
			defaultContracts.find((entry: any) => entry._id === selectedContractId),
		[defaultContracts, selectedContractId]
	)
	const selectedContractDeliveryZones = useMemo(
		() => selectedContract?.deliveryZones || [],
		[selectedContract]
	)

	// Automatic zone: the area Uber accepts for this contract, a circle of the
	// Uber radius around its pickup point. The API resolves the same circle from
	// UBER_GEO_ZONE_ID, so this geojson only has to draw it.
	const uberGeoZone = useMemo(() => {
		const area: UberDeliveryArea | null =
			selectedContract?.uberDeliveryArea || null
		if (!area) return null

		return {
			_id: UBER_GEO_ZONE_ID,
			name: Strings.orders.geoZoneUberLimits.replace(
				"{0}",
				String(area.radiusKm)
			),
			geojson: circleToFeatureCollection(
				{ lat: area.lat, lng: area.lng },
				area.radiusKm
			),
			automatic: true,
		}
	}, [selectedContract])

	// The automatic zone leads the contract's own zones in the filter dropdown.
	const geoZoneOptions = useMemo(
		() => (uberGeoZone ? [uberGeoZone, ...geoZones] : geoZones),
		[uberGeoZone, geoZones]
	)

	// The zone currently filtered by, drawn on the order maps.
	const selectedGeoZone = useMemo(
		() =>
			geoZoneOptions.find(
				(zone: any) => zone._id === attributeFilters.geoZoneId
			) || null,
		[geoZoneOptions, attributeFilters.geoZoneId]
	)

	return (
		<div className="ScreenOrders">
			{/* Helmet for SEO and meta tags */}
			{/* TODO: Investigate and check if this is needed for an internal app */}
			<Helmet>
				<title>{Strings.sidebar.orders}</title>
				<meta
					name="description"
					content="Manage your orders and their status"
				/>
			</Helmet>
			<ContentWrapper extraClass="OrdersScreenWrapper">
				<div className="OrdersScreen" ref={containerRef}>
					<div
						className={`OrdersScreen__table${selectedOrders.length > 0 ? " OrdersScreen__table--with-panel" : ""}`}
						style={
							selectedOrders.length > 0
								? { flex: `0 0 ${tableWidth}%` }
								: undefined
						}
					>
						<ContentTable
							orders={orders}
							pagination={pagination}
							setPagination={setPagination}
							activeTab={activeTab}
							showImportModal={showImportModal}
							setShowImportModal={setShowImportModal}
							setShowAddressValidationModal={setShowAddressValidationModal}
							selectedOrders={selectedOrders}
							setSelectedOrders={setSelectedOrders}
							search={search}
							setSearch={setSearch}
							defaultContracts={defaultContracts}
							defaultLots={defaultLots}
							selectedStatus={selectedStatus}
							setSelectedStatus={setSelectedStatus}
							attributeFilters={attributeFilters}
							setAttributeFilters={setAttributeFilters}
							geoZones={geoZoneOptions}
							contractSelected={!!orderFilters?.contract}
							refreshOrders={() =>
								getOrders(
									dispatch,
									selectedStatus,
									search,
									orderFilters?.business,
									orderFilters?.contract,
									orderFilters?.date,
									selectedLot,
									pagination,
									setPagination,
									setDefaultLots,
									attributeFilters
								)
							}
						/>
					</div>
					{selectedOrders.length > 0 && (
						<>
							<div
								className="OrdersScreen__resizer"
								onMouseDown={handleMouseDown}
								onDoubleClick={handleDoubleClick}
							/>
							<div
								className="OrdersScreen__panel"
								style={{ flex: `0 0 ${100 - tableWidth}%` }}
							>
								{selectedOrders.length === 1 ? (
									<OrderDetailPanel
										orderId={selectedOrders[0]}
										orders={orders}
										setOrders={handleSetOrders}
										onClose={handlePanelClose}
										onChangeLot={handleChangeLot}
										onValidateAddress={setShowAddressValidationModal}
										waitForModalConfirmation={handleModalConfirmation}
										deliveryZones={selectedContractDeliveryZones}
										filterZone={selectedGeoZone}
									/>
								) : (
									<MultiOrderPanel
										selectedOrders={filteredSelectedOrders}
										totalOrders={orders.length}
										allOrders={orders}
										setOrders={handleSetOrders}
										onClose={handlePanelClose}
										onChangeLot={handleChangeLot}
										onRemoveOrder={(orderId) =>
											setSelectedOrders((prev) =>
												prev.filter((id) => id !== orderId)
											)
										}
										onSelectOrders={setSelectedOrders}
										deliveryZones={selectedContractDeliveryZones}
										filterZone={selectedGeoZone}
										waitForModalConfirmation={handleModalConfirmation}
									/>
								)}
							</div>
						</>
					)}
				</div>
				<ImportModal
					showImportModal={showImportModal}
					setShowImportModal={setShowImportModal}
					business={defaultBusinesses.find(
						(entry: any) => entry._id === orderFilters?.business
					)}
					contract={defaultContracts.find(
						(entry: any) => entry._id === orderFilters?.contract
					)}
					getOrders={() =>
						getOrders(
							dispatch,
							selectedStatus,
							search,
							orderFilters?.business,
							orderFilters?.contract,
							orderFilters?.date,
							selectedLot,
							pagination,
							setPagination,
							setDefaultLots,
							attributeFilters
						)
					}
				/>
				<AddressValidationModal
					orders={orders}
					showAddressValidationModal={showAddressValidationModal}
					setShowAddressValidationModal={setShowAddressValidationModal}
					business={orderFilters?.business}
					contract={orderFilters?.contract}
					deliveryZones={selectedContractDeliveryZones}
					selectedStatus={selectedStatus}
					date={orderFilters?.date}
					pagination={pagination}
					setPagination={setPagination}
					search={search}
					attributeFilters={attributeFilters}
					setDefaultLots={setDefaultLots}
					setShowApprovalModal={setShowApprovalModal}
					setApprovalData={setApprovalData}
				/>
				{showConfirmModal && (
					<ConfirmationMapModal
						orders={orders}
						contract={defaultContracts.find(
							(entry: any) => entry._id === orderFilters?.contract
						)}
						selectedOrders={selectedOrders}
						setShowConfirmModal={handleSetShowConfirmModal}
						onConfirm={handleConfirmModal}
					/>
				)}
				<ApprovalModal
					visible={showApprovalModal}
					data={approvalData}
					onCancel={() => {
						setShowApprovalModal(false)
						setApprovalData(null)
					}}
					onConfirm={async (selectedOrderIds: string[]) => {
						if (!approvalData) return

						dispatch(setLoader(true))

						try {
							// Call API with selected orders
							const response = await callAddressValidationAPI({
								orderId: approvalData.originalOrderId,
								// Backend validates/stores the formatted (editable) address.
								originalAddress: approvalData.formValues.formattedAddress,
								formattedAddress: approvalData.formValues.formattedAddress,
								addressNotes: approvalData.formValues.addressNotes,
								coordinates: {
									lat: approvalData.formValues.lat,
									lng: approvalData.formValues.lng,
								},
								postalCode: approvalData.formValues.postalCode,
								city: approvalData.formValues.city,
								country: approvalData.formValues.country,
								lot: approvalData.formValues.lot,
								relatedOrders: selectedOrderIds,
							})

							// Always close the modal regardless of response
							setShowApprovalModal(false)
							setApprovalData(null)

							if (response.ok) {
								notification.success({
									message: Strings.orders.addressConfirmation,
									description:
										response?.data?.message || "Orders updated successfully.",
									placement: "bottomRight",
									duration: 5,
								})

								// Refresh orders
								getOrders(
									dispatch,
									selectedStatus,
									search,
									orderFilters?.business,
									orderFilters?.contract,
									orderFilters?.date,
									null,
									pagination,
									setPagination,
									setDefaultLots,
									attributeFilters
								)
							} else {
								notification.error({
									message: Strings.orders.addressValidationError,
									description:
										response?.data?.message || Strings.serverErrors.wentWrong,
									placement: "bottomRight",
									duration: 5,
								})
							}
						} catch (error) {
							// Always close the modal even on error
							setShowApprovalModal(false)
							setApprovalData(null)

							notification.error({
								message: Strings.orders.addressValidationError,
								description: "An error occurred while updating orders.",
								placement: "bottomRight",
								duration: 5,
							})
						} finally {
							dispatch(setLoader(false))
						}
					}}
				/>
				<BulkLotUpdateModal
					visible={showBulkLotUpdateModal}
					onCancel={() => setShowBulkLotUpdateModal(false)}
					selectedOrders={selectedOrders}
					orders={orders}
					onConfirm={async (newLot: string) => {
						dispatch(setLoader(true))

						try {
							const response = await API.put({
								url: Endpoints.uriOrders("bulk"),
								data: {
									orders: selectedOrders.map((id) => ({
										orderId: id,
										lot: newLot,
									})),
								},
							})

							if (response.ok) {
								notification.success({
									message: Strings.sidebar.orders,
									description:
										response?.data?.message || "Lot updated successfully.",
									placement: "bottomRight",
									duration: 5,
								})

								// Refresh orders
								getOrders(
									dispatch,
									selectedStatus,
									search,
									orderFilters?.business,
									orderFilters?.contract,
									orderFilters?.date,
									selectedLot,
									pagination,
									setPagination,
									setDefaultLots,
									attributeFilters
								)

								// Close modal and clear selection
								setShowBulkLotUpdateModal(false)
								setSelectedOrders([])
							} else {
								notification.error({
									message: Strings.sidebar.orders,
									description:
										response?.data?.message || Strings.serverErrors.wentWrong,
									placement: "bottomRight",
									duration: 5,
								})
							}
						} catch (error) {
							notification.error({
								message: Strings.sidebar.orders,
								description: "An error occurred while updating lot.",
								placement: "bottomRight",
								duration: 5,
							})
						} finally {
							dispatch(setLoader(false))
						}
					}}
				/>
				{deleteModal}
			</ContentWrapper>
		</div>
	)
}

async function getInitialData(
	dispatch: any,
	selectedBusiness: any,
	setDefaultBusinesses: (businesses: any[]) => void,
	setDefaultContracts: (contracts: any[]) => void
) {
	dispatch(setLoader(true))

	let response: any
	try {
		response = await API.get({
			url: Endpoints.uriBusinesses("orders"),
		})

		if (response.ok) {
			const { businesses: defaultBusinesses } = response.data.results || {}

			let defaultContracts: any[] = []
			if (selectedBusiness) {
				const tempBusiness = defaultBusinesses.find(
					(entry: any) => entry._id === selectedBusiness
				)

				if (tempBusiness) {
					defaultContracts = JSON.parse(
						JSON.stringify(tempBusiness.contracts || [])
					)
				}
			}

			setDefaultBusinesses(defaultBusinesses)
			setDefaultContracts(defaultContracts)
		} else {
			notification.error({
				message: Strings.sidebar.orders,
				description: response?.data?.message || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		}
	} catch (err) {
		notification.error({
			message: Strings.serverErrors.title,
			description: Strings.serverErrors.wentWrong,
			placement: "bottomRight",
			duration: 5,
		})
	}

	dispatch(setLoader(false))
}

const BulkLotUpdateModal: React.FC<{
	visible: boolean
	onCancel: () => void
	selectedOrders: string[]
	orders: any[]
	onConfirm: (newLot: string) => void
}> = ({ visible, onCancel, selectedOrders, orders, onConfirm }) => {
	const mobile = useSelector((state: any) => state.mobile)
	const loading = useSelector((state: any) => state.loading)
	const [newLot, setNewLot] = useState<string>("")

	// Reset lot input when modal opens
	useEffect(() => {
		if (visible) {
			setNewLot("")
		}
	}, [visible])

	const orderCount = selectedOrders.length

	return (
		<Modal
			title={Strings.orders.updateLotTitle}
			visible={visible}
			onCancel={onCancel}
			footer={[
				<Button key="cancel" onClick={onCancel} disabled={loading}>
					{Strings.generic.cancel}
				</Button>,
				<Button
					key="confirm"
					type="primary"
					loading={loading}
					onClick={() => onConfirm(newLot)}
					disabled={loading || !newLot.trim()}
				>
					{Strings.orders.updateLotButton.replace("{0}", String(orderCount))}
				</Button>,
			]}
			width={mobile ? "100%" : 600}
		>
			<div>
				<div style={{ marginBottom: 16 }}>
					<Icon
						name="info-2"
						style={{ color: "#1890ff", marginRight: 8, fontSize: 16 }}
					/>
					<strong>{Strings.orders.updateLotHeader}</strong>
				</div>

				<p style={{ marginBottom: 16 }}>
					{Strings.orders.updateLotBody.replace("{0}", String(orderCount))}
				</p>

				<Form.Item
					label={Strings.orders.updateLotNewLot}
					required
					style={{ marginBottom: 16 }}
				>
					<Input
						placeholder={Strings.orders.updateLotPlaceholder}
						value={newLot}
						onChange={(e) => setNewLot(e.target.value)}
						onPressEnter={() => {
							if (newLot.trim()) {
								onConfirm(newLot)
							}
						}}
						autoFocus
					/>
				</Form.Item>

				<div style={{ marginBottom: 16 }}>
					<strong>{Strings.orders.updateLotSelected}</strong>
				</div>
				<div
					style={{
						maxHeight: "200px",
						overflowY: "auto",
						padding: "8px",
						backgroundColor: "#fafafa",
						border: "1px solid #d9d9d9",
						borderRadius: "4px",
					}}
				>
					{selectedOrders.map((orderId) => {
						const order = orders.find((o) => o._id === orderId)
						return (
							<div
								key={orderId}
								style={{
									padding: "6px 12px",
									marginBottom: "4px",
									backgroundColor: "#fff",
									border: "1px solid #e8e8e8",
									borderRadius: "2px",
									fontSize: "13px",
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
								}}
							>
								<span style={{ fontWeight: 500 }}>
									{order?.orderId || orderId}
								</span>
								<span style={{ color: "#8c8c8c", fontSize: "12px" }}>
									{Strings.orders.updateLotCurrent} {order?.lot || "N/A"}
								</span>
							</div>
						)
					})}
				</div>
			</div>
		</Modal>
	)
}

const ApprovalModal: React.FC<{
	visible: boolean
	data: {
		originalOrderId: string
		relatedOrders: Array<{
			orderId: string
			orderAddress: any
			formattedAddress: any
			bundleId?: string
			user?: any
		}>
		message: string
		formValues?: any
	} | null
	onCancel: () => void
	onConfirm: (selectedOrderIds: string[]) => void
}> = ({ visible, data, onCancel, onConfirm }) => {
	const mobile = useSelector((state: any) => state.mobile)
	const loading = useSelector((state: any) => state.loading)
	const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([])

	// Reset selection when modal opens
	useEffect(() => {
		if (visible && data) {
			// Pre-select all orders by default, original order is always selected
			setSelectedOrderIds(data.relatedOrders.map((o) => o.orderId))
		}
	}, [visible, data])

	if (!data) return null

	const { originalOrderId, relatedOrders, message } = data

	const toggleOrderSelection = (orderId: string) => {
		// Prevent deselecting the original order
		if (orderId === originalOrderId) return

		setSelectedOrderIds((prev) =>
			prev.includes(orderId)
				? prev.filter((id) => id !== orderId)
				: [...prev, orderId]
		)
	}

	const originalOrder = relatedOrders.find((o) => o.orderId === originalOrderId)

	return (
		<Modal
			title="Confirm Orders to Update"
			visible={visible}
			onCancel={onCancel}
			footer={[
				<Button key="cancel" onClick={onCancel} disabled={loading}>
					{Strings.generic.cancel}
				</Button>,
				<Button
					key="confirm"
					type="primary"
					loading={loading}
					onClick={() => onConfirm(selectedOrderIds)}
					disabled={
						loading ||
						selectedOrderIds.length === 0 ||
						!selectedOrderIds.includes(originalOrderId)
					}
				>
					Update Selected Orders ({selectedOrderIds.length})
				</Button>,
			]}
			width={mobile ? "100%" : 900}
		>
			<div style={{ marginBottom: 16, display: "flex", alignItems: "center" }}>
				<Icon
					name="info-2"
					style={{ color: "#faad14", marginRight: 8, fontSize: 16 }}
				/>
				<strong>Multiple Related Orders Found</strong>
			</div>

			<p style={{ marginBottom: 16 }}>{message}</p>

			<div style={{ marginBottom: 16 }}>
				<strong>New Address:</strong>
				<div
					style={{
						background: "#f6f6f6",
						padding: "8px 12px",
						borderRadius: "4px",
						marginTop: "4px",
						fontFamily: "monospace",
					}}
				>
					{data.formValues?.formattedAddress || "N/A"}
				</div>
			</div>

			<div>
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						marginBottom: "8px",
					}}
				>
					<strong>Select Orders to Update:</strong>
					<div>
						<Button
							size="small"
							onClick={() =>
								setSelectedOrderIds(relatedOrders.map((o) => o.orderId))
							}
							style={{ marginRight: 8 }}
						>
							Select All
						</Button>
						<Button
							size="small"
							onClick={() => setSelectedOrderIds([originalOrderId])}
						>
							Deselect All
						</Button>
					</div>
				</div>

				<div
					style={{
						maxHeight: "400px",
						overflowY: "auto",
						border: "1px solid #d9d9d9",
						borderRadius: "4px",
					}}
				>
					{/* Original Order */}
					<div
						style={{
							padding: "12px",
							borderBottom:
								relatedOrders.length > 0 ? "1px solid #f0f0f0" : "none",
							backgroundColor: "#e6f7ff", // Always selected appearance
							cursor: "not-allowed", // Indicate it's not clickable
						}}
					>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								marginBottom: "4px",
							}}
						>
							<input
								type="checkbox"
								checked={true} // Always checked
								disabled={true} // Disabled to prevent interaction
								style={{ marginRight: "8px", opacity: 0.6 }}
							/>
							<div style={{ fontWeight: 500, color: "#1890ff" }}>
								<span role="img" aria-label="package">
									📦
								</span>{" "}
								{originalOrderId} (Original Order)
							</div>
						</div>
						<div
							style={{ fontSize: "12px", color: "#666", marginLeft: "24px" }}
						>
							Current Address: {originalOrder?.orderAddress?.address || "N/A"}
						</div>
						<div
							style={{ fontSize: "12px", color: "#666", marginLeft: "24px" }}
						>
							Current Formatted Address:{" "}
							{originalOrder?.formattedAddress?.address || "N/A"}
						</div>
					</div>

					{/* Related Orders */}
					{relatedOrders
						.filter((o) => o.orderId !== originalOrderId)
						.map((relatedOrder, index) => (
							<div
								key={relatedOrder.orderId}
								style={{
									padding: "12px",
									borderBottom:
										index < relatedOrders.length - 2
											? "1px solid #f0f0f0"
											: "none",
									backgroundColor: selectedOrderIds.includes(
										relatedOrder.orderId
									)
										? "#f6ffed"
										: "#fff",
									cursor: "pointer",
								}}
								onClick={() => toggleOrderSelection(relatedOrder.orderId)}
							>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										marginBottom: "4px",
									}}
								>
									<input
										type="checkbox"
										checked={selectedOrderIds.includes(relatedOrder.orderId)}
										onChange={() => toggleOrderSelection(relatedOrder.orderId)}
										style={{ marginRight: "8px" }}
									/>
									<div style={{ fontWeight: 500 }}>
										<span role="img" aria-label="package">
											📦
										</span>{" "}
										{relatedOrder.orderId}
									</div>
								</div>
								<div
									style={{
										fontSize: "12px",
										color: "#666",
										marginLeft: "24px",
									}}
								>
									Current Address: {relatedOrder.orderAddress?.address || "N/A"}
								</div>
								<div
									style={{
										fontSize: "12px",
										color: "#666",
										marginLeft: "24px",
									}}
								>
									Current Formatted Address:{" "}
									{relatedOrder.formattedAddress?.address || "N/A"}
								</div>
							</div>
						))}
				</div>
			</div>
		</Modal>
	)
}

export default Orders

// TODO: Organize following functions and components into their own files
